import React from "react";
import { useTranslation } from "react-i18next";
import {
    updateConsent,
    persistConsent,
    loadConsent,
    categoriesToConsent,
    DEFAULT_CATEGORIES,
    ALL_GRANTED,
    ALL_DENIED,
} from "@/utils/consent";

function Switch({ checked, onChange, locked, testId }) {
    return (
        <button
            type="button"
            className={`aurx-switch ${locked ? "locked" : checked ? "on" : ""}`}
            onClick={locked ? undefined : () => onChange(!checked)}
            aria-pressed={checked}
            aria-disabled={locked}
            data-testid={testId}
        />
    );
}

export default function CookieBanner() {
    const { t } = useTranslation();
    const [visible, setVisible] = React.useState(false);
    const [showPrefs, setShowPrefs] = React.useState(false);
    const [prefs, setPrefs] = React.useState(DEFAULT_CATEGORIES);

    // Init on mount: apply saved consent if present, otherwise show banner
    React.useEffect(() => {
        const saved = loadConsent();
        if (saved && saved.preferences) {
            setPrefs(saved.preferences);
            updateConsent(categoriesToConsent(saved.preferences));
        } else {
            setVisible(true);
        }
    }, []);

    // Listen for external "open preferences" event (from footer)
    React.useEffect(() => {
        const handler = () => {
            const saved = loadConsent();
            if (saved && saved.preferences) {
                setPrefs(saved.preferences);
            }
            setShowPrefs(true);
            setVisible(true);
        };
        window.addEventListener(
            "auronixra:open-cookie-preferences",
            handler,
        );
        return () =>
            window.removeEventListener(
                "auronixra:open-cookie-preferences",
                handler,
            );
    }, []);

    const acceptAll = () => {
        const next = {
            necessary: true,
            functionality: true,
            analytics: true,
            advertising: true,
            personalization: true,
        };
        setPrefs(next);
        updateConsent(ALL_GRANTED);
        persistConsent(next);
        setVisible(false);
        setShowPrefs(false);
    };

    const rejectAll = () => {
        const next = { ...DEFAULT_CATEGORIES };
        setPrefs(next);
        updateConsent(ALL_DENIED);
        persistConsent(next);
        setVisible(false);
        setShowPrefs(false);
    };

    const savePreferences = () => {
        updateConsent(categoriesToConsent(prefs));
        persistConsent(prefs);
        setVisible(false);
        setShowPrefs(false);
    };

    const setCat = (key, value) => setPrefs((p) => ({ ...p, [key]: value }));

    if (!visible) return null;

    if (showPrefs) {
        const cats = t("cookies.categories", { returnObjects: true });
        return (
            <div
                className="aurx-modal-backdrop"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cookie-modal-title"
                data-testid="cookie-preferences-modal"
            >
                <div className="aurx-modal">
                    <h3 id="cookie-modal-title">{t("cookies.banner.title")}</h3>
                    <p className="p-body">{t("cookies.banner.body")}</p>

                    <div className="aurx-cookie-cat">
                        <div className="cat-body">
                            <h4>{cats.necessary.title}</h4>
                            <p>{cats.necessary.body}</p>
                        </div>
                        <Switch
                            checked={true}
                            locked
                            testId="cookie-necessary"
                        />
                    </div>

                    <div className="aurx-cookie-cat">
                        <div className="cat-body">
                            <h4>{cats.functionality.title}</h4>
                            <p>{cats.functionality.body}</p>
                        </div>
                        <Switch
                            checked={prefs.functionality}
                            onChange={(v) => setCat("functionality", v)}
                            testId="cookie-functionality"
                        />
                    </div>

                    <div className="aurx-cookie-cat">
                        <div className="cat-body">
                            <h4>{cats.analytics.title}</h4>
                            <p>{cats.analytics.body}</p>
                        </div>
                        <Switch
                            checked={prefs.analytics}
                            onChange={(v) => setCat("analytics", v)}
                            testId="cookie-analytics"
                        />
                    </div>

                    <div className="aurx-cookie-cat">
                        <div className="cat-body">
                            <h4>{cats.advertising.title}</h4>
                            <p>{cats.advertising.body}</p>
                        </div>
                        <Switch
                            checked={prefs.advertising}
                            onChange={(v) => setCat("advertising", v)}
                            testId="cookie-advertising"
                        />
                    </div>

                    <div className="aurx-cookie-cat">
                        <div className="cat-body">
                            <h4>{cats.personalization.title}</h4>
                            <p>{cats.personalization.body}</p>
                        </div>
                        <Switch
                            checked={prefs.personalization}
                            onChange={(v) => setCat("personalization", v)}
                            testId="cookie-personalization"
                        />
                    </div>

                    <div
                        className="aurx-cookie-actions"
                        style={{ marginTop: 24 }}
                    >
                        <button
                            onClick={rejectAll}
                            className="aurx-btn aurx-btn-ghost"
                            data-testid="cookie-reject"
                        >
                            {t("cookies.banner.reject")}
                        </button>
                        <button
                            onClick={savePreferences}
                            className="aurx-btn aurx-btn-primary"
                            data-testid="cookie-save"
                        >
                            {t("cookies.banner.save")}
                        </button>
                        <button
                            onClick={acceptAll}
                            className="aurx-btn aurx-btn-gold"
                            data-testid="cookie-accept"
                        >
                            {t("cookies.banner.accept")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="aurx-cookie" role="dialog" aria-live="polite" data-testid="cookie-banner">
            <h3>{t("cookies.banner.title")}</h3>
            <p>{t("cookies.banner.body")}</p>
            <div className="aurx-cookie-actions">
                <button
                    onClick={acceptAll}
                    className="aurx-btn aurx-btn-gold"
                    data-testid="cookie-accept-all"
                >
                    {t("cookies.banner.accept")}
                </button>
                <button
                    onClick={rejectAll}
                    className="aurx-btn aurx-btn-ghost"
                    data-testid="cookie-reject-all"
                >
                    {t("cookies.banner.reject")}
                </button>
                <button
                    onClick={() => setShowPrefs(true)}
                    className="aurx-btn aurx-btn-ghost"
                    data-testid="cookie-manage"
                >
                    {t("cookies.banner.manage")}
                </button>
            </div>
        </div>
    );
}
